import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { CreditCard, ArrowsClockwise, ArrowSquareOut, Receipt, Gear, ArrowUUpLeft, Bank } from "@phosphor-icons/react";
import { fmtDate } from "../lib/dateUtil";

const money = (v) => `£${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const REASONS = [["requested_by_customer", "Requested by customer"], ["duplicate", "Duplicate payment"], ["fraudulent", "Fraudulent"]];

function Tile({ label, value, sub, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <p className="text-2xl font-bold font-[Chivo] mt-1">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function StatusBadge({ tx }) {
  if (tx.payment_status === "refunded") return <Badge variant="outline" className="text-red-700 border-red-300" data-testid="funds-badge">Refunded</Badge>;
  if (tx.payment_status === "partially_refunded") return <Badge variant="outline" className="text-amber-700 border-amber-300" data-testid="funds-badge">Partly refunded {money(tx.refunded_amount)}</Badge>;
  if (tx.payment_status !== "paid") {
    return <Badge variant="outline" className="text-muted-foreground" data-testid="funds-badge">{tx.payment_status === "expired" ? "Expired" : "Awaiting payment"}</Badge>;
  }
  if (tx.payout_id) return <Badge className="bg-emerald-600 text-white" data-testid="funds-badge">{tx.payout_status === "paid" ? "Paid out" : "In payout"}</Badge>;
  if (tx.funds_status === "available") return <Badge className="bg-emerald-600 text-white" data-testid="funds-badge">Available in Stripe</Badge>;
  if (tx.funds_status === "pending") return <Badge className="bg-amber-500 text-white" data-testid="funds-badge">Pending{tx.funds_available_on ? ` · ${fmtDate(tx.funds_available_on)}` : ""}</Badge>;
  return <Badge variant="secondary" data-testid="funds-badge">Paid</Badge>;
}

function PayoutStatus({ status }) {
  const map = { paid: ["Paid", "bg-emerald-600 text-white"], in_transit: ["In transit", "bg-sky-600 text-white"], pending: ["Pending", "bg-amber-500 text-white"],
    failed: ["Failed", "bg-red-600 text-white"], canceled: ["Cancelled", "bg-muted text-muted-foreground"] };
  const [label, cls] = map[status] || [status || "—", "bg-muted"];
  return <Badge className={cls} data-testid="payout-status-badge">{label}</Badge>;
}

function RefundDialog({ tx, onClose, onDone }) {
  const [amount, setAmount] = useState(tx ? String(tx.refundable) : "");
  const [reason, setReason] = useState("requested_by_customer");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (tx) { setAmount(String(tx.refundable)); setReason("requested_by_customer"); } }, [tx]);
  if (!tx) return null;
  const full = Number(amount) >= tx.refundable - 0.001;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(`/payments/card/${tx.id}/refund`, { amount: Number(amount), reason });
      toast.success(full ? `Refunded ${money(amount)} — invoice ${tx.invoice_reference || ""} reopened` : `Refunded ${money(amount)} (partial)`);
      onDone(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="refund-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowUUpLeft size={18} /> Refund card payment</DialogTitle>
          <DialogDescription>
            {tx.invoice_reference || "Invoice"} · {tx.client_name || "client"} · paid {money(tx.amount)}{tx.refunded_amount > 0 ? ` (${money(tx.refunded_amount)} already refunded)` : ""}.
            The money goes back to the card in 5–10 working days. Stripe's processing fee is not returned.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold">Amount to refund (max {money(tx.refundable)})</Label>
            <Input type="number" step="0.01" min="0.01" max={tx.refundable} value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" data-testid="refund-amount-input" />
            <div className="flex gap-2 mt-1.5">
              <Button type="button" size="sm" variant={full ? "default" : "outline"} onClick={() => setAmount(String(tx.refundable))} data-testid="refund-full-btn">Full refund</Button>
              <Button type="button" size="sm" variant={!full ? "default" : "outline"} onClick={() => setAmount((tx.refundable / 2).toFixed(2))} data-testid="refund-half-btn">Half</Button>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="mt-1" data-testid="refund-reason-select"><SelectValue /></SelectTrigger>
              <SelectContent>{REASONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <p className="text-[11px] rounded-md bg-muted/40 px-3 py-2" data-testid="refund-effect-note">
            {full ? "Full refund: the invoice reopens (back to sent/overdue) and your admins are notified." : "Partial refund: the invoice stays paid with a note of the amount returned."}
            {tx.gateway === "connect" && tx.platform_fee > 0 && " The platform fee is returned to you pro-rata."}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="refund-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={busy || !(Number(amount) > 0) || Number(amount) > tx.refundable + 0.001} className="bg-red-600 hover:bg-red-700 text-white" data-testid="refund-confirm-btn">
            {busy ? "Refunding…" : `Refund ${money(amount || 0)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayoutsSection({ payouts, syncing, onSync, canSync }) {
  return (
    <div className="space-y-2" data-testid="payouts-section">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base md:text-lg font-semibold flex items-center gap-2"><Bank size={18} /> Payouts to your bank</h2>
          <p className="text-[11px] text-muted-foreground">
            Each batch Stripe sends to your bank account.{payouts?.last_synced_at ? ` Last synced ${fmtDate(payouts.last_synced_at)}.` : ""}
            {payouts?.last_error && <span className="text-red-600"> {payouts.last_error}</span>}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={onSync} disabled={syncing || !canSync} className="gap-1" data-testid="sync-payouts-btn">
          <ArrowsClockwise size={14} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync payouts"}
        </Button>
      </div>
      <div className="rounded-lg border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="payouts-table">
          <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Arrives</th>
              <th className="text-right px-3 py-2 font-semibold">Amount</th>
              <th className="text-left px-3 py-2 font-semibold">Status</th>
              <th className="text-right px-3 py-2 font-semibold">Card payments</th>
              <th className="text-left px-3 py-2 font-semibold">Method</th>
              <th className="text-right px-3 py-2 font-semibold">Stripe</th>
            </tr>
          </thead>
          <tbody>
            {(!payouts || payouts.items.length === 0) && (
              <tr><td colSpan={6} className="text-center py-6 text-muted-foreground text-sm" data-testid="payouts-empty">
                {canSync ? "No payouts yet — Stripe pays out on your account's schedule once funds become available." : "Connect Stripe to see payouts here."}
              </td></tr>
            )}
            {payouts?.items.map((p) => (
              <tr key={p.id} className="border-t border-border" data-testid={`payout-row-${p.id}`}>
                <td className="px-3 py-2 whitespace-nowrap">{p.arrival_date ? fmtDate(p.arrival_date) : "—"}</td>
                <td className="px-3 py-2 text-right font-medium">{money(p.amount)}</td>
                <td className="px-3 py-2"><PayoutStatus status={p.status} /></td>
                <td className="px-3 py-2 text-right">{p.payments_count || 0}{p.payments_amount ? <span className="text-muted-foreground text-xs"> · {money(p.payments_amount)}</span> : null}</td>
                <td className="px-3 py-2 text-muted-foreground">{p.method || "standard"}</td>
                <td className="px-3 py-2 text-right">{p.dashboard_url && <a href={p.dashboard_url} target="_blank" rel="noreferrer" className="inline-flex p-1 rounded hover:bg-muted" data-testid="payout-stripe-link"><ArrowSquareOut size={15} /></a>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function CardPaymentsPage() {
  const [data, setData] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [days, setDays] = useState("30");
  const [refreshing, setRefreshing] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [refundTx, setRefundTx] = useState(null);

  const load = useCallback(async (d) => {
    try {
      const { data: res } = await API.get("/payments/card", { params: { days: d } });
      setData(res);
    } catch { toast.error("Failed to load card payments"); }
  }, []);
  const loadPayouts = useCallback(async (sync = 1) => {
    try {
      const { data: res } = await API.get("/payments/card/payouts", { params: { sync } });
      setPayouts(res);
    } catch { /* payouts are secondary */ }
  }, []);

  useEffect(() => { load(days); }, [days, load]);
  useEffect(() => { loadPayouts(1); }, [loadPayouts]);

  const patchRow = (fresh) => setData((prev) => ({ ...prev, items: prev.items.map((t) => (t.id === fresh.id ? fresh : t)) }));

  const refresh = async (tx) => {
    setRefreshing(tx.id);
    try {
      const { data: fresh } = await API.post(`/payments/card/${tx.id}/refresh`);
      patchRow(fresh);
      toast.success(`Refreshed ${fresh.invoice_reference || "payment"} from Stripe`);
      load(days);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Refresh failed";
      toast.error(msg);
      setData((prev) => ({ ...prev, items: prev.items.map((t) => (t.id === tx.id ? { ...t, refresh_error: msg } : t)) }));
    }
    finally { setRefreshing(""); }
  };

  const syncPayouts = async () => {
    setSyncing(true);
    try {
      const { data: r } = await API.post("/payments/card/payouts/sync");
      toast.success(`Synced ${r.payouts} payout${r.payouts === 1 ? "" : "s"} from Stripe${r.mapped ? ` · ${r.mapped} payment${r.mapped === 1 ? "" : "s"} matched` : ""}`);
      await loadPayouts(0);
      load(days);
    } catch (e) { toast.error(e?.response?.data?.detail || "Payout sync failed"); }
    finally { setSyncing(false); }
  };

  if (!data) return <div className="text-center py-12 text-muted-foreground text-sm">Loading card payments...</div>;
  const { summary: s, items, gateway } = data;

  return (
    <div className="space-y-6" data-testid="card-payments-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight flex items-center gap-2"><CreditCard size={28} weight="duotone" className="text-primary" /> Card Payments</h1>
          <p className="text-sm text-muted-foreground mt-1">Invoices your clients paid by card through Stripe — fees, net amounts, refunds and payouts.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-36" data-testid="card-payments-range"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last 12 months</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" asChild data-testid="card-payments-settings-link"><Link to="/settings?tab=card-payments"><Gear size={15} className="mr-1" /> Settings</Link></Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs rounded-lg border border-border bg-muted/30 px-3 py-2" data-testid="card-payments-gateway">
        {gateway?.active ? (
          <>
            <Badge variant="default">{gateway.active === "connect" ? "Stripe Connect" : "Own Stripe keys"}</Badge>
            {gateway.account_name && <span className="text-muted-foreground">{gateway.account_name}</span>}
            {gateway.stripe_mode === "test" && <Badge variant="outline" className="text-amber-700 border-amber-300">Test mode</Badge>}
            {gateway.platform_fee_pct > 0 && <span className="text-muted-foreground">· {gateway.platform_fee_pct}% platform fee per payment</span>}
          </>
        ) : (
          <>
            <Badge variant="secondary">Card payments off</Badge>
            <span className="text-muted-foreground">Clients see bank-transfer details only.</span>
            <Link to="/settings?tab=card-payments" className="text-primary underline" data-testid="card-payments-enable-link">Turn on card payments</Link>
          </>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label={`Received · ${s.days}d`} value={money(s.gross)} sub={`${s.count} payment${s.count === 1 ? "" : "s"}${s.refunded > 0 ? ` · ${money(s.refunded)} refunded` : ""}`} testid="tile-gross" />
        <Tile label="Stripe fees" value={money(s.fees)} sub={s.platform_fees > 0 ? `incl. ${money(s.platform_fees)} platform fee` : "Processing fees"} testid="tile-fees" />
        <Tile label="Net to you" value={money(s.net)} sub="After fees and refunds" testid="tile-net" />
        <Tile label="Pending funds" value={money(s.pending_funds)} sub="Not yet paid out by Stripe" testid="tile-pending" />
      </div>

      <div className="rounded-lg border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="card-payments-table">
          <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Date</th>
              <th className="text-left px-3 py-2 font-semibold">Invoice</th>
              <th className="text-left px-3 py-2 font-semibold">Client</th>
              <th className="text-right px-3 py-2 font-semibold">Amount</th>
              <th className="text-right px-3 py-2 font-semibold">Fee</th>
              <th className="text-right px-3 py-2 font-semibold">Net</th>
              <th className="text-left px-3 py-2 font-semibold">Card</th>
              <th className="text-left px-3 py-2 font-semibold">Status</th>
              <th className="text-left px-3 py-2 font-semibold">Payout</th>
              <th className="text-right px-3 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={10} className="text-center py-10 text-muted-foreground text-sm" data-testid="card-payments-empty">No card payments yet. Once a client pays an invoice by card it appears here within seconds.</td></tr>
            )}
            {items.map((tx) => (
              <tr key={tx.id} className="border-t border-border hover:bg-muted/20" data-testid={`card-payment-row-${tx.id}`}>
                <td className="px-3 py-2 whitespace-nowrap">{fmtDate(tx.paid_at || tx.created_at)}</td>
                <td className="px-3 py-2">
                  {tx.invoice_id ? <Link to={`/invoices/${tx.invoice_id}`} className="font-mono text-primary hover:underline" data-testid="card-payment-invoice-link">{tx.invoice_reference || "Invoice"}</Link> : <span className="font-mono">{tx.invoice_reference}</span>}
                </td>
                <td className="px-3 py-2 truncate max-w-[160px]">{tx.client_name || "—"}</td>
                <td className="px-3 py-2 text-right font-medium">{money(tx.amount)}{tx.refunded_amount > 0 && <span className="block text-[10px] text-red-600">−{money(tx.refunded_amount)} refunded</span>}</td>
                <td className="px-3 py-2 text-right text-muted-foreground">
                  {tx.fee != null ? money(tx.fee) : "—"}
                  {tx.platform_fee > 0 && <span className="block text-[10px]">incl. {money(tx.platform_fee)} platform</span>}
                </td>
                <td className="px-3 py-2 text-right font-medium">{tx.net != null ? money(tx.net - (tx.refunded_amount || 0) + (tx.platform_fee_refunded || 0)) : (["paid", "partially_refunded", "refunded"].includes(tx.payment_status) ? money(tx.amount - (tx.refunded_amount || 0)) : "—")}</td>
                <td className="px-3 py-2 whitespace-nowrap">{tx.card_last4 ? `${(tx.card_brand || "card").toUpperCase()} •••• ${tx.card_last4}` : "—"}</td>
                <td className="px-3 py-2"><StatusBadge tx={tx} /></td>
                <td className="px-3 py-2 whitespace-nowrap text-xs" data-testid="card-payment-payout-cell">
                  {tx.payout_id ? (
                    <span title={tx.payout_id}>{tx.payout_arrival_date ? fmtDate(tx.payout_arrival_date) : "Scheduled"}<span className="block text-[10px] text-muted-foreground">{tx.payout_status === "paid" ? "paid out" : tx.payout_status?.replace("_", " ")}</span></span>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-1">
                    {tx.refundable > 0 && ["paid", "partially_refunded"].includes(tx.payment_status) && (
                      <button type="button" onClick={() => setRefundTx(tx)} className="p-1 rounded hover:bg-muted text-red-600" title="Refund" data-testid="card-payment-refund-btn"><ArrowUUpLeft size={15} /></button>
                    )}
                    {tx.dashboard_url && <a href={tx.dashboard_url} target="_blank" rel="noreferrer" className="p-1 rounded hover:bg-muted" title="Open in Stripe" data-testid="card-payment-stripe-link"><ArrowSquareOut size={15} /></a>}
                    {tx.receipt_url && <a href={tx.receipt_url} target="_blank" rel="noreferrer" className="p-1 rounded hover:bg-muted" title="Stripe receipt" data-testid="card-payment-receipt-link"><Receipt size={15} /></a>}
                    <button type="button" onClick={() => refresh(tx)} disabled={refreshing === tx.id} className="p-1 rounded hover:bg-muted disabled:opacity-50" title="Refresh from Stripe" data-testid="card-payment-refresh-btn">
                      <ArrowsClockwise size={15} className={refreshing === tx.id ? "animate-spin" : ""} />
                    </button>
                  </div>
                  {tx.refresh_error && <p className="text-[10px] text-red-600 text-right mt-0.5">{tx.refresh_error}</p>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PayoutsSection payouts={payouts} syncing={syncing} onSync={syncPayouts} canSync={!!gateway?.active} />

      <p className="text-[11px] text-muted-foreground">
        "Status" shows whether Stripe has released the money into your balance or paid it out; "Payout" is the bank transfer that carried it. All-time: {s.all_time_count} payment{s.all_time_count === 1 ? "" : "s"} · {money(s.all_time_gross)}.
      </p>

      <RefundDialog tx={refundTx} onClose={() => setRefundTx(null)} onDone={(fresh) => { patchRow(fresh); setRefundTx(null); load(days); }} />
    </div>
  );
}
